/**
 * SpaceCardV2 - Versión simplificada sin precios para exploración
 * Muestra información básica del espacio sin tarifas ni disponibilidad
 */
import React, { useState, useEffect } from 'react'
import { useTheme } from '../../hooks/useTheme'
import SafeComodidades from '../common/SafeComodidades'
import { getImageUrl } from '../../utils/imageUtils'

interface SpaceCardV2Props {
  espacio: {
    id: string
    codigo: string
    nombre: string
    descripcion: string
    tipo?: string
    capacidad_maxima: number
    comodidades: (string | { nombre: string; icono?: string })[]
    imagenes: string[]
    reservable?: boolean
  }
  onViewDetails: (id: string) => void
  className?: string
}

// Imagen por defecto
const DEFAULT_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNzUgMTIwSDE3MEMxNjcuMjM5IDEyMCAxNjUgMTIyLjIzOSAxNjUgMTI1VjE3NUMxNjUgMTc3Ljc2MSAxNjcuMjM5IDE4MCAxNzAgMTgwSDE3NUMxNzcuNzYxIDE4MCAxODAgMTc3Ljc2MSAxODAgMTc1VjEyNUMxODAgMTIyLjIzOSAxNzcuNzYxIDEyMCAxNzUgMTIwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjAwIDEyMEgxOTVDMTkyLjIzOSAxMjAgMTkwIDEyMi4yMzkgMTkwIDEyNVYxNzVDMTkwIDE3Ny43NjEgMTkyLjIzOSAxODAgMTk1IDE4MEgyMDBDMjAyLjc2MSAxODAgMjA1IDE3Ny43NjEgMjA1IDE3NVYxMjVDMjA1IDEyMi4yMzkgMjAyLjc2MSAxMjAgMjAwIDEyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeD0iMTgwIiB5PSIxMzAiPgo8Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iIzZCNzI4MCIvPgo8cGF0aCBkPSJNMTYuNjY2NyAyMy4zMzMzTDIwIDIwTDIzLjMzMzMgMjMuMzMzM00yMCAxNi42NjY3VjIzLjMzMzMiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo8dGV4dCB4PSIyMDAiIHk9IjIwMCIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2QjcyODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkVzcGFjaW8gZGUgQ2FtcGluZzwvdGV4dD4KPC9zdmc+'

// Helper functions
const getTypeIcon = (tipo?: string): string => {
  if (!tipo) return '🏕️'
  switch (tipo.toLowerCase()) {
    case 'cabaña':
    case 'cabana':
      return '🏠'
    case 'camping':
      return '⛺'
    case 'glamping':
      return '🏕️'
    case 'casa':
      return '🏡'
    case 'meson':
    case 'mesón':
      return '🍽️'
    default:
      return '🏕️'
  }
}

const getTypeColor = (tipo?: string): string => {
  if (!tipo) return 'from-camping-100 to-camping-200 text-camping-700'
  switch (tipo.toLowerCase()) {
    case 'cabaña':
    case 'cabana':
      return 'from-camping-200 to-camping-300 text-camping-700'
    case 'camping':
      return 'from-camping-100 to-camping-200 text-camping-700'
    case 'glamping':
      return 'from-camping-100 to-camping-200 text-camping-700'
    case 'casa':
      return 'from-camping-50 to-camping-100 text-camping-600'
    default:
      return 'from-camping-100 to-camping-200 text-camping-700'
  }
}

const SpaceCardV2: React.FC<SpaceCardV2Props> = ({
  espacio,
  onViewDetails,
  className = ''
}) => {
  const { colors } = useTheme()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [imageError, setImageError] = useState(false)

  // Auto-rotar imágenes cada 5 segundos
  useEffect(() => {
    if (espacio.imagenes && espacio.imagenes.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % espacio.imagenes.length)
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [espacio.imagenes])

  const currentImage = espacio.imagenes && espacio.imagenes.length > 0
    ? getImageUrl(espacio.imagenes[currentImageIndex])
    : DEFAULT_IMAGE

  return (
    <div className={`espacio-card group bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${className}`}>
      {/* Imagen */}
      <div className="relative h-48 sm:h-56 md:h-64 bg-gray-200 overflow-hidden">
        <img
          src={imageError ? DEFAULT_IMAGE : currentImage}
          alt={espacio.nombre}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          onError={() => setImageError(true)}
        />

        {/* Badge de tipo */}
        {espacio.tipo && (
          <div className={`absolute top-4 left-4 bg-gradient-to-r ${getTypeColor(espacio.tipo)} px-3 py-1 rounded-full text-sm font-semibold shadow-lg backdrop-blur-sm`}>
            <span className="mr-1">{getTypeIcon(espacio.tipo)}</span>
            {espacio.tipo}
          </div>
        )}

        {/* Contador de imágenes */}
        {espacio.imagenes && espacio.imagenes.length > 1 && (
          <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
            {currentImageIndex + 1} / {espacio.imagenes.length}
          </div>
        )}
      </div>

      {/* Contenido */}
      <div className="p-4 sm:p-6">
        {/* Título y descripción */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-900 mb-1">{espacio.nombre}</h3>
          <p className="text-gray-600 text-sm line-clamp-2">{espacio.descripcion}</p>
        </div>

        {/* Info row: Código + Capacidad */}
        <div className="flex items-center gap-4 text-sm text-gray-700 mb-4">
          <div className="flex items-center gap-1">
            <span>📍</span>
            <span className="font-medium">{espacio.codigo}</span>
          </div>
          <div className="flex items-center gap-1">
            <span>👥</span>
            <span>{espacio.capacidad_maxima} personas</span>
          </div>
        </div>

        {/* Comodidades */}
        <div className="mb-4">
          <SafeComodidades comodidades={espacio.comodidades} />
        </div>

        {/* Botón */}
        <button
          onClick={() => onViewDetails(espacio.id)}
          className="w-full px-6 py-3 rounded-lg text-white font-semibold transition-all duration-200 hover:shadow-lg"
          style={{
            backgroundColor: colors.primary
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.primaryDark
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.primary
          }}
        >
          Ver Detalles y Reservar
        </button>
      </div>
    </div>
  )
}

export default SpaceCardV2
